import { useEventListener } from "@vueuse/core";
import { computed, ref, type ComputedRef, type Ref } from "vue";

// ITDEVELOP-3147 — one source of truth for the responsive model. The portal
// has exactly three viewport classes, and everything responsive derives from
// this table:
//
//   class    | overlays          | shell
//   ---------|-------------------|----------
//   desktop  | desktop overlays  | side menu
//   tablet   | desktop overlays  | burger
//   phone    | sheet overlays    | burger
//
// The tablet row is DELIBERATE (ticket ruling, 2026-08-20): it composes the
// unchanged mobile shell with the unchanged desktop overlays, so it carries no
// surfaces of its own. Call sites never branch on the class directly — they
// use the derived composables below, each named for the one decision it makes.

export type ViewportClass = "phone" | "tablet" | "desktop";

/** Overlays render sized for a phone (sheets, full-screen tiers) or for
 * desktop (popovers, floating panels). Tablets take the desktop size. */
export type OverlaySize = "phone" | "desktop";

const PHONE_MAX = 768; // Tailwind md: below it is a phone
const TABLET_MAX = 1024; // above it the shell keeps the side menu

function classify(width: number): ViewportClass {
  if (width < PHONE_MAX) return "phone";
  if (width > TABLET_MAX) return "desktop";
  return "tablet";
}

export function useViewportClass(): Ref<ViewportClass> {
  const viewportClass = ref<ViewportClass>(classify(window.innerWidth));

  useEventListener(window, "resize", () => {
    viewportClass.value = classify(window.innerWidth);
  });

  return viewportClass;
}

/** The layout decision: side menu (true) or burger shell (false). */
export function useIsDesktop(): ComputedRef<boolean> {
  const viewportClass = useViewportClass();
  return computed(() => viewportClass.value === "desktop");
}

/** The overlay decision: which size of overlay surfaces to render. */
export function useOverlaySize(): ComputedRef<OverlaySize> {
  const viewportClass = useViewportClass();
  return computed(() => (viewportClass.value === "phone" ? "phone" : "desktop"));
}
